import { useState, type FormEvent } from 'react'
import { Church, KeyRound, LockKeyhole, Mail } from 'lucide-react'
import { useAuth } from '../auth/AuthProvider'

export function LoginPage() {
  const { configured, signIn, sendOtp, verifyOtp, requestPasswordReset } = useAuth()
  const [mode, setMode] = useState<'password' | 'otp' | 'reset'>('password')
  const [resetSent, setResetSent] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [token, setToken] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      if (mode === 'reset') {
        await requestPasswordReset(email.trim())
        setResetSent(true)
      } else if (mode === 'password') await signIn(email.trim(), password)
      else if (!otpSent) {
        await sendOtp(email.trim())
        setOtpSent(true)
      } else await verifyOtp(email.trim(), token.trim())
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Sign in failed.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="login-page">
      <section className="login-intro"><div className="brand-mark large" aria-hidden="true"><Church size={28} strokeWidth={1.8} /></div><p className="eyebrow">St. Vincent de Paul</p><h1>Cemetery records, made clear.</h1><p>Find plots, connect people, and maintain accurate records from one secure workspace.</p></section>
      <section className="login-panel">
        <form className="login-form" onSubmit={submit}>
          <div><p className="eyebrow">Staff access</p><h2>{mode === 'reset' ? 'Reset your password' : 'Sign in to continue'}</h2><p>{mode === 'reset' ? 'Enter your email to request a password reset link.' : 'Use your existing cemetery records account.'}</p></div>
          {!configured && <div className="configuration-warning"><strong>Configuration required</strong><span>Add the Supabase URL and publishable key to your environment before signing in.</span></div>}
          <div className="segmented-control" aria-label="Sign-in method">
            <button type="button" className={mode === 'password' ? 'selected' : ''} onClick={() => { setMode('password'); setOtpSent(false) }}><LockKeyhole size={15} /> Password</button>
            <button type="button" className={mode === 'otp' ? 'selected' : ''} onClick={() => setMode('otp')}><KeyRound size={15} /> One-time code</button>
          </div>
          <label>Email address<div className="input-with-icon"><Mail size={17} /><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="email" /></div></label>
          {mode === 'password' ? <label>Password<div className="input-with-icon"><LockKeyhole size={17} /><input type="password" value={password} onChange={(event) => setPassword(event.target.value)} required autoComplete="current-password" /></div></label> : mode === 'otp' ? otpSent ? <label>One-time code<input value={token} onChange={(event) => setToken(event.target.value)} inputMode="numeric" required autoComplete="one-time-code" /></label> : <p className="form-note">A one-time code will be sent to your email address.</p> : null}
          {mode === 'reset' && resetSent && <p className="form-note" role="status">If an account exists for this email, you will receive a password reset link. Check your inbox and spam folder.</p>}
          {error && <div className="form-error" role="alert">{error}</div>}
          <button className="primary-button" type="submit" disabled={!configured || submitting}>{submitting ? 'Please wait…' : mode === 'reset' ? 'Send reset link' : mode === 'password' ? 'Sign in' : otpSent ? 'Verify code' : 'Send code'}</button>
          <button type="button" disabled={submitting} onClick={() => { setMode(mode === 'reset' ? 'password' : 'reset'); setError(null); setResetSent(false) }}>{mode === 'reset' ? 'Back to sign in' : 'Forgot password?'}</button>
        </form>
      </section>
    </div>
  )
}
